import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import { alpha, useTheme } from '@mui/material/styles';
import {
  formatDateOnly,
  isAnnouncementActive,
  parseDateValue,
} from '../../utils/notificationUtils';

function getPreview(text, max = 180) {
  const value = String(text || '').trim();
  if (!value) return 'Stay tuned for the latest company update.';
  if (value.length <= max) return value;
  return `${value.slice(0, max).trimEnd()}...`;
}

function getWindowLabel(announcement) {
  const start = parseDateValue(announcement?.start_date);
  const end = parseDateValue(announcement?.end_date);

  if (start && end) {
    return `${formatDateOnly(start)} - ${formatDateOnly(end)}`;
  }

  if (start) {
    return `Active from ${formatDateOnly(start)}`;
  }

  return 'Currently active';
}

export default function AnnouncementAlertBanner({ announcements = [] }) {
  const theme = useTheme();

  const activeAnnouncements = useMemo(
    () =>
      announcements.filter((announcement) => isAnnouncementActive(announcement)),
    [announcements]
  );

  const leadAnnouncement = activeAnnouncements[0] || null;

  if (!leadAnnouncement) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        mb: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: alpha(theme.palette.error.main, 0.18),
        background: `linear-gradient(135deg, ${alpha(theme.palette.error.light, 0.22)} 0%, ${alpha(theme.palette.warning.light, 0.22)} 52%, ${alpha(theme.palette.background.paper, 0.92)} 100%)`,
        boxShadow: `0 18px 38px ${alpha(theme.palette.error.main, 0.08)}`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -46,
          right: -24,
          width: 136,
          height: 136,
          borderRadius: '50%',
          backgroundColor: alpha(theme.palette.warning.main, 0.12),
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -58,
          left: -18,
          width: 116,
          height: 116,
          borderRadius: '50%',
          backgroundColor: alpha(theme.palette.error.main, 0.08),
        }}
      />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ position: 'relative', zIndex: 1, px: 1.8, py: 1.55 }}
      >
        <Stack spacing={0.8} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.7 }}>
            <Chip
              icon={<NotificationsActiveRoundedIcon sx={{ fontSize: 16 }} />}
              label="Announcement Alert"
              size="small"
              sx={{
                fontWeight: 900,
                color: theme.palette.error.dark,
                backgroundColor: alpha(theme.palette.background.paper, 0.58),
              }}
            />
            {activeAnnouncements.length > 1 ? (
              <Chip
                size="small"
                variant="outlined"
                label={`${activeAnnouncements.length} active now`}
                sx={{
                  fontWeight: 800,
                  backgroundColor: alpha(theme.palette.background.paper, 0.42),
                }}
              />
            ) : null}
          </Stack>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }} noWrap>
              {leadAnnouncement.title || 'Latest Announcement'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              {getPreview(leadAnnouncement.message || leadAnnouncement.content)}
            </Typography>
          </Box>
        </Stack>

        <Stack
          spacing={0.45}
          sx={{
            minWidth: { xs: '100%', md: 196 },
            px: 1.2,
            py: 0.95,
            borderRadius: 2.5,
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(10px)',
          }}
        >
          <Stack direction="row" spacing={0.8} alignItems="center">
            <CampaignRoundedIcon sx={{ fontSize: 17, color: 'error.main' }} />
            <Typography variant="caption" sx={{ fontWeight: 900, letterSpacing: 0.8 }}>
              LIVE NOTICE
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {getWindowLabel(leadAnnouncement)}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}
